import React, { useState, useEffect, useRef, useCallback } from 'react';
import IntroSection from './IntroSection';
import LifeSection from './LifeSection';
import WriteSection from './WriteSection';
import GridLayout from './GridLayout';
import SpeakSection from './SpeakSection';
import Footer from './Footer';
import PortfolioPuzzle from "./PortfolioPuzzle";
import NavigationBar from "./NavigationBar";

const LanguageGreeting = () => {
    // State management
    const [isDarkMode, setIsDarkMode] = useState(true);
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [currentTime, setCurrentTime] = useState("");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Refs
    const textAreaRef = useRef(null);
    const overlayRef = useRef(null);

    // Mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Time updates
    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
            const istOffset = 5.5 * 60 * 60000;
            const istTime = new Date(utc + istOffset);
            const hours = istTime.getHours();
            const minutes = istTime.getMinutes();
            const seconds = istTime.getSeconds();
            const ampm = hours >= 12 ? 'pm' : 'am';
            const formattedHours = hours % 12 || 12;
            const timeString = `${formattedHours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ${ampm} ist`;
            setCurrentTime(timeString);
        };

        updateTime();
        const interval = setInterval(updateTime, 1000);
        return () => clearInterval(interval);
    }, []);

    // Cursor movement
    useEffect(() => {
        if (!isMobile) {
            const updateCursorPosition = (e) => {
                setCursorPosition({ x: e.clientX, y: e.clientY });
            };
            window.addEventListener('mousemove', updateCursorPosition);
            return () => window.removeEventListener('mousemove', updateCursorPosition);
        }
    }, [isMobile]);

    // Message handling
    const handleSendMessage = async (e) => {
        e.preventDefault();
        try {
            const mailtoLink = `mailto:pushpalathanshenoy@gmail.com?subject=Portfolio Contact&body=${encodeURIComponent(message)}`;
            window.location.href = mailtoLink;
            setTimeout(() => {
                setMessage("");
                setIsOverlayOpen(false);
            }, 500);
        } catch (error) {
            console.error('Error sending message:', error);
            alert('There was an error sending your message. Please try again.');
        }
    };

    const handleMessageChange = useCallback((e) => {
        e.preventDefault();
        setMessage(e.target.value);
    }, []);

    const toggleOverlay = useCallback((value) => {
        setIsOverlayOpen(value);
        if (value && textAreaRef.current) {
            setTimeout(() => {
                textAreaRef.current.focus();
            }, 100);
        }
    }, []);

    // Components
    const MobileMenu = () => (
        <div className={`fixed inset-0 bg-black/95 z-50 transform transition-all duration-300 
                    ${isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'}`}>
            <div className="flex flex-col items-center justify-center h-full space-y-8 font-mono">
                <button className="text-2xl opacity-80 hover:opacity-100">Work</button>
                <button className="text-2xl opacity-80 hover:opacity-100">About</button>
                <button className="text-2xl opacity-80 hover:opacity-100">Contact</button>
                <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="mt-12 text-lg opacity-60 hover:opacity-100"
                >
                    Close
                </button>
            </div>
        </div>
    );

    const Overlay = () => {
        useEffect(() => {
            if (isOverlayOpen && textAreaRef.current) {
                textAreaRef.current.focus();
            }
        }, [isOverlayOpen]);

        if (!isOverlayOpen) return null;

        return (
            <div
                ref={overlayRef}
                className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center"
                onClick={(e) => {
                    if (e.target === overlayRef.current) {
                        toggleOverlay(false);
                    }
                }}
            >
                <div
                    className="w-full max-w-2xl mx-4 bg-black/50 rounded-lg p-8"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="mb-8">
                        <h2 className="text-2xl md:text-3xl mb-4 font-mono">Let's talk</h2>
                        <p className="text-gray-400 font-mono">Share your thoughts or just say hi!</p>
                    </div>

                    <form onSubmit={handleSendMessage} className="space-y-8">
                        <div className="relative">
                            <textarea
                                ref={textAreaRef}
                                value={message}
                                onChange={handleMessageChange}
                                className="w-full h-32 bg-transparent border-b border-gray-700
                                         text-white font-mono resize-none focus:outline-none
                                         focus:border-white placeholder-gray-600 pb-4
                                         focus:ring-0"
                                placeholder="Type your message..."
                                maxLength={500}
                                style={{ caretColor: 'white' }}
                            />
                            <div className="absolute bottom-2 right-2 text-gray-600 font-mono text-sm">
                                {message.length} / 500
                            </div>
                        </div>

                        <div className="flex justify-between items-center">
                            <button
                                type="button"
                                onClick={() => toggleOverlay(false)}
                                className="font-mono text-white hover:text-gray-400
                                         transition-colors flex items-center space-x-2"
                            >
                                <span>←</span>
                                <span>go back</span>
                            </button>
                            <button
                                type="submit"
                                disabled={!message.trim()}
                                className="font-mono px-4 md:px-6 py-2 md:py-3 border rounded-full
                                         text-white hover:bg-white hover:text-black transition-all
                                         duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Send message
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-white text-black'} 
                        ${!isMobile ? 'cursor-none' : ''}`}>
            {/* Navigation Bar */}
            <NavigationBar
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                currentTime={currentTime}
                setIsMobileMenuOpen={setIsMobileMenuOpen}
            />

            {/* Mobile Menu */}
            {isMobile && <MobileMenu />}

            {/* Custom Cursor */}
            {!isMobile && (
                <div
                    className="fixed pointer-events-none w-8 h-8 bg-gray-400/50
                              rounded-full mix-blend-difference"
                    style={{
                        transform: `translate(${cursorPosition.x - 16}px, ${cursorPosition.y - 16}px)`,
                        transition: 'transform 0.1s ease-out',
                        zIndex: 9999
                    }}
                />
            )}

            {/* Main content */}
            <main className="py-20 md:py-32 px-4 md:px-32">
                {/* Intro section */}
                <IntroSection
                    isDarkMode={isDarkMode}
                    toggleOverlay={toggleOverlay}
                    isMobile={isMobile}
                />

                {/* Portfolio sections */}
                <div className="space-y-16 md:space-y-32">
                    <GridLayout isDarkMode={isDarkMode} />
                    <WriteSection isDarkMode={isDarkMode} />
                    <LifeSection isDarkMode={isDarkMode} />
                    <PortfolioPuzzle isDarkMode={isDarkMode} />
                    <SpeakSection isDarkMode={isDarkMode} />
                </div>

                {/* Footer */}
                <Footer />
            </main>

            {/* Contact overlay */}
            <Overlay />
        </div>
    );
};

export default LanguageGreeting;